import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { toast } from 'vue-sonner'
import { axios } from '@/lib'
import type { MarkReadRequest, UpdateChannelsRequest, ListWrap, Notification } from './types'

export function useMarkNotificationsReadMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const response = await axios.post<void>('/api/notifications/read', {
        Data: notificationIds,
      } as MarkReadRequest)
      return response.data
    },
    // Optimistic update: set Read=true in cache
    onMutate: async (notificationIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'], exact: false })

      const previous = queryClient.getQueriesData<ListWrap<Notification>>({
        queryKey: ['notifications'],
        exact: false,
      })

      previous.forEach(([key, old]) => {
        if (!old) return
        const next: ListWrap<Notification> = {
          ...old,
          Data: (old.Data || []).map((n) =>
            notificationIds.includes(n.Id) ? { ...n, Read: true } : n,
          ),
        }
        queryClient.setQueryData(key as unknown as readonly unknown[], next)
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, old]: [unknown, ListWrap<Notification> | undefined]) => {
        if (old) queryClient.setQueryData(key as unknown as readonly unknown[], old)
      })
      toast.error('Failed to mark notifications as read')
    },
  })
}

export function useMarkNotificationsUnreadMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const response = await axios.post<void>('/api/notifications/unread', {
        Data: notificationIds,
      } as MarkReadRequest)
      return response.data
    },
    // Optimistic update: set Read=false in cache
    onMutate: async (notificationIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'], exact: false })

      const previous = queryClient.getQueriesData<ListWrap<Notification>>({
        queryKey: ['notifications'],
        exact: false,
      })

      previous.forEach(([key, old]) => {
        if (!old) return
        const next: ListWrap<Notification> = {
          ...old,
          Data: (old.Data || []).map((n) =>
            notificationIds.includes(n.Id) ? { ...n, Read: false } : n,
          ),
        }
        queryClient.setQueryData(key as unknown as readonly unknown[], next)
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, old]: [unknown, ListWrap<Notification> | undefined]) => {
        if (old) queryClient.setQueryData(key as unknown as readonly unknown[], old)
      })
      toast.error('Failed to mark notifications as unread')
    },
  })
}

export function useUpdateNotificationChannelsMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (channelNames: string[]) => {
      const response = await axios.post<void>('/api/notifications/channels', {
        Data: channelNames,
      } as UpdateChannelsRequest)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] })
      toast.success('Notification channels updated', { position: 'bottom-right' })
    },
    onError: () => {
      toast.error('Failed to update notification channels')
    },
  })
}
